import { useState, useEffect } from 'react'
import { useUserRegion } from './useUserRegion'

interface PricingState {
  formattedPrice: string
  formattedOriginalPrice: string
  currency: string
  isLoading: boolean
}

// Regional multipliers based on purchasing power
const REGION_MULTIPLIERS: Record<string, number> = {
  // Developed Markets (1.0)
  US: 1.0,
  CA: 1.0,
  AU: 1.0,
  NZ: 1.0,
  GB: 1.0,
  DE: 1.0,
  FR: 1.0,
  IT: 1.0,
  ES: 1.0,
  NL: 1.0,
  BE: 1.0,
  AT: 1.0,
  IE: 1.0,
  FI: 1.0,
  SE: 1.0,
  DK: 1.0,
  SG: 1.0,
  AE: 1.0,
  CH: 1.0,
  NO: 1.0,

  // Mid-tier Markets (0.9)
  PL: 0.9,
  GR: 0.9,
  PT: 0.9,
  MY: 0.9,
  TH: 0.9,
  MX: 0.9,
  BR: 0.9,
  CL: 0.9,
  CR: 0.9,
  UY: 0.9,

  // Developing Markets (0.7)
  AR: 0.7,
  CO: 0.7,
  PE: 0.7,
  IN: 0.7,
  ID: 0.7,
  PH: 0.7,
  VN: 0.7,
  PK: 0.7,
  BD: 0.7,
  NG: 0.7,
  KE: 0.7,
  EG: 0.7,
  ZA: 0.7,
  EC: 0.7,
  BO: 0.7,
  PY: 0.7,
  HN: 0.7,
  GT: 0.7,
  SV: 0.7,
  NI: 0.7,
}

export function usePricing(
  basePriceUSD = 17.99,
  originalPriceUSD = 59.99,
): PricingState {
  const { countryCode, currency, isLoading: isRegionLoading } = useUserRegion()
  const [pricing, setPricing] = useState<PricingState>({
    formattedPrice: '$17.99',
    formattedOriginalPrice: '$59.99',
    currency: 'USD',
    isLoading: true,
  })

  useEffect(() => {
    if (isRegionLoading) return

    const calculatePrice = async () => {
      try {
        // Default to USD if currency is USD
        if (currency === 'USD') {
          setPricing({
            formattedPrice: '$17.99',
            formattedOriginalPrice: '$59.99',
            currency: 'USD',
            isLoading: false,
          })
          return
        }

        // Check for cached rates
        let rates = null
        const cachedRates = localStorage.getItem('exchange_rates')
        const cacheTimestamp = localStorage.getItem('exchange_rates_timestamp')
        const isCacheValid =
          cacheTimestamp &&
          Date.now() - parseInt(cacheTimestamp) < 24 * 60 * 60 * 1000 // 24h cache

        if (cachedRates && isCacheValid) {
          rates = JSON.parse(cachedRates)
        } else {
          try {
            const response = await fetch(
              'https://api.exchangerate.host/latest?base=USD',
            )
            if (response.ok) {
              const data = await response.json()
              rates = data.rates
              localStorage.setItem('exchange_rates', JSON.stringify(rates))
              localStorage.setItem(
                'exchange_rates_timestamp',
                Date.now().toString(),
              )
            }
          } catch (e) {
            console.error('Error fetching rates', e)
          }
        }

        if (!rates || !rates[currency]) {
          // Fallback to USD if rate not found
          setPricing({
            formattedPrice: '$17.99',
            formattedOriginalPrice: '$59.99',
            currency: 'USD',
            isLoading: false,
          })
          return
        }

        const rate = rates[currency]
        const multiplier = REGION_MULTIPLIERS[countryCode] || 0.7 // Default to 0.7 for unknown/developing

        const finalPrice = basePriceUSD * rate * multiplier
        const finalOriginalPrice = originalPriceUSD * rate * multiplier

        const formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })

        setPricing({
          formattedPrice: formatter.format(finalPrice),
          formattedOriginalPrice: formatter.format(finalOriginalPrice),
          currency,
          isLoading: false,
        })
      } catch (error) {
        console.error('Pricing calculation error:', error)
        setPricing({
          formattedPrice: '$17.99',
          formattedOriginalPrice: '$59.99',
          currency: 'USD',
          isLoading: false,
        })
      }
    }

    calculatePrice()
  }, [countryCode, currency, isRegionLoading, basePriceUSD, originalPriceUSD])

  return pricing
}
